import { AbstractComponent } from "appworks/components/abstract-component";
import { FlexiText } from "appworks/graphics/elements/flexi-text";
import { Layers } from "appworks/graphics/layers/layers";
import { Result } from "appworks/model/gameplay/records/results/result";
import { CurrencyService } from "appworks/services/currency/currency-service";
import { Services } from "appworks/services/services";
import { TransactionService } from "appworks/services/transaction/transaction-service";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { Contract } from "appworks/utils/contracts/contract";
import { gameiomModel } from "gameiom/model/gameiom-model";
import { FreespinWinResult } from "slotworks/model/gameplay/records/results/freespin-win-result";
import { LineResult } from "slotworks/model/gameplay/records/results/line-result";
import { WaysResult } from "slotworks/model/gameplay/records/results/ways-result";
import { SlotBetService } from "slotworks/services/bet/slot-bet-service";

export enum FooterMessageKey {
    DEFAULT = "fot.new",
    NO_WIN = "fot.no",
    SPIN_START = "fot.pla",
    FREESPIN_START = "fot.fpl",
    GAME_WIN = "fot.gam",
    LINE_WIN = "fot.lin",
    WAY_WIN = "fot.way",
    WAYS_WIN = "fot.ways",
    WAY_TRIGGER = "fot.tri",
    WAYS_TRIGGER = "fot.trs",
    RETRIGGER = "fot.ret",
    FREESPINS = "fot.fre"
}

export interface FooterComponentConfig {
    layer?: Layers;
    // Optional; pass this through to currency format whenever it is used in this component
    forceCreditMode?: boolean;
}

export class FooterComponent extends AbstractComponent {
    public hideTotalWin: boolean = false;

    protected layer: Layers;

    protected textValue: string = "";

    protected textPortrait: FlexiText;
    protected textLandscape: FlexiText;

    protected config?: FooterComponentConfig = {
    }

    constructor(config?: Partial<FooterComponentConfig>) {
        super();

        Services.get(SlotBetService).onBetChange.add(() => this.clear());

        if (config) {
            this.config = { ...this.config, ...config };
        }

        this.layer = this.config.layer || Layers.get("Footer");
        this.layer.onSceneEnter.add(() => this.refresh());
        Services.get(TransactionService).currentWinUpdate.add((value: number) => {
            if (value <= 0 && !this.textPortrait && !this.textLandscape) {
                this.clear();
            }
        });

        this.layer.defaultScene().execute();

        this.refresh();
    }

    public showNoWin() {
        this.clear();
    }

    public showSpinStart(betValue: number) {
        this.clear();
    }

    public showFreespinStart() {
        this.clear();
    }

    public showGameWin(winValue: number) {
        if (!this.hideTotalWin) {
            this.setText(Services.get(TranslationsService).get(FooterMessageKey.GAME_WIN, { win: Services.get(CurrencyService).format(winValue, !gameiomModel.shouldDisplayCredits(), this.getForceCreditMode()) }));
        }
        return Contract.empty();
    }

    public showLineWin(result: LineResult, symbol?: string) {
        const win = Services.get(CurrencyService).format(result.cashWon, !gameiomModel.shouldDisplayCredits(), this.getForceCreditMode());
        this.setText(Services.get(TranslationsService).get(FooterMessageKey.LINE_WIN, { line: result.line + 1, linewin: win, win, matches: result.matches, symbol }));
        return Contract.empty();
    }

    public showWaysWin(result: WaysResult, symbol: string) {
        const winData = {
            payout_per_symbols: Services.get(CurrencyService).format(result.perWayPayout, !gameiomModel.shouldDisplayCredits(), this.getForceCreditMode()),
            number_of_ways: result.ways,
            total_payout: Services.get(CurrencyService).format(result.cashWon, !gameiomModel.shouldDisplayCredits(), this.getForceCreditMode()),
            triggered: 0
        };

        if (result.linkedResult instanceof FreespinWinResult) {
            winData.triggered = result.linkedResult.freespinsWon;
        }

        let message = result.ways > 1 ? FooterMessageKey.WAYS_WIN : FooterMessageKey.WAY_WIN;

        if (winData.triggered > 0) {
            message = result.ways > 1 ? FooterMessageKey.WAYS_TRIGGER : FooterMessageKey.WAY_TRIGGER;
        }

        this.setText(Services.get(TranslationsService).get(message, winData));
    }

    public showMiscWin(textId: string, result: Result) {
        this.setText(Services.get(TranslationsService).get(textId));
    }

    public showBigWinMessage(tier: number) {
        // Custom implementation
    }

    public clear() {
        this.setText("");
    }

    public getForceCreditMode(): boolean {
        return this.config.forceCreditMode || gameiomModel.shouldDisplayCredits();
    }

    protected setText(text: string) {
        this.textValue = text;
        if (this.textLandscape) {
            this.textLandscape.setText(text);
        }
        if (this.textPortrait) {
            this.textPortrait.setText(text);
        }
    }

    protected refresh() {
        this.textLandscape = this.layer.getFlexiText("value_landscape");
        this.textPortrait = this.layer.getFlexiText("value_portrait");
        this.setText(this.textValue);
    }
}
